import type { AircraftOverviewColumn, AircraftOverviewRow } from "src/shared/api-types/certificate";

import { groupRowsByUser } from "src/components/AircraftOverviewTable";
import {
  aggregatedStatusColours,
  headerCell,
  remainingDays,
  statusCell,
  worstStatusFirst,
  type ExcelRow,
  type ExcelSheet,
  type ExportedStatus,
} from "src/utils/excelExport";

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Every operation spans three columns: its status, under supervision and the limitations
const OPERATION_COLUMN_WIDTHS = [14, 18, 30];

// Mirrors AircraftOverviewTable: one row per aircraft, the user name merged over the user's aircraft rows
export const buildAircraftOverviewSheet = (
  columns: AircraftOverviewColumn[],
  rows: AircraftOverviewRow[],
  t: Translate,
): ExcelSheet => {
  const groups = groupRowsByUser(rows);

  const headerRows: ExcelRow[] = [
    [
      headerCell(`${t("user")} (${groups.length})`, { rowSpan: 2 }),
      headerCell(t("compliance.aircraft"), { rowSpan: 2 }),
      ...columns.flatMap((column) => [
        headerCell(column.operationName, { columnSpan: OPERATION_COLUMN_WIDTHS.length, align: "center" }),
        null,
        null,
      ]),
    ],
    [
      null,
      null,
      ...columns.flatMap(() => [
        headerCell(t("status")),
        headerCell(t("aircraft_management.under_supervision")),
        headerCell(t("limitations.title")),
      ]),
    ],
  ];

  const userStatuses = (group: (typeof groups)[number]): ExportedStatus[] =>
    group.rows.flatMap((row) =>
      columns.map((column) => {
        const combined = row.operationCompliance[column.key]?.combined;
        return { cell: statusCell(combined?.status, t), validUntil: combined?.validUntil };
      }),
    );

  // The users with the worst status come first. The merged user name has the colour of the worst status over all
  // the user's aircraft.
  const bodyRows: ExcelRow[] = worstStatusFirst(groups, userStatuses).flatMap((group) =>
    group.rows.map(
      (row, rowIndex): ExcelRow => [
        rowIndex === 0
          ? {
              value: group.userName,
              rowSpan: group.rows.length,
              alignVertical: "top",
              ...aggregatedStatusColours(userStatuses(group).map(({ cell }) => cell)),
            }
          : null,
        { value: row.aircraftName },
        ...columns.flatMap((column) => {
          const compliance = row.operationCompliance[column.key];
          const limitations = compliance?.limitations ?? [];
          return [
            statusCell(compliance?.combined.status, t, remainingDays(compliance?.combined.validUntil)),
            compliance?.underSupervision ? { value: t("yes"), align: "center" as const } : null,
            limitations.length > 0
              ? { value: limitations.map((limitation) => limitation.displayValue).join("; ") }
              : null,
          ];
        }),
      ],
    ),
  );

  return {
    headerRows,
    bodyRows,
    columnWidths: [30, 20, ...columns.flatMap(() => OPERATION_COLUMN_WIDTHS)],
  };
};
